import { IMagazinePage } from 'src/@interfaces'

export const FIRST_PAGE = 0

export const isFirstPageIndex = (pageIndex: number) => pageIndex === FIRST_PAGE

export const isSecondPageIndex = (pageIndex: number) => pageIndex === FIRST_PAGE + 1

export const isPenultimate = (pageIndex: number, length: number) => pageIndex === length - 2

export const getPageShownAtTimeCount = (isMobile: boolean) => (isMobile ? 1 : 2)

export const mapPagesIndexesById = (pages: IMagazinePage[]) =>
  pages.reduce((pagesMap, page, index) => pagesMap.set(page.id, index), new Map<number, number>([]))

/**
 * Get the difference between current page index and it's sibling page index.
 *
 * @example
 * getSiblingPageDeltaIndex(1) === -1 // if sibling page is in the left
 * getSiblingPageDeltaIndex(1) === 1 // if it's in the right
 *
 * @returns {number} the difference between current page index and it's sibling page index.
 */
export const getSiblingPageDeltaIndex = (currentSlide: number): number => {
  const currentPageIsEven = currentSlide % 2 === 0
  const currentSlideIsFirstPage = isFirstPageIndex(currentSlide)

  const previousPageIndex = -1
  const nextPageIndex = 1

  return !currentSlideIsFirstPage && currentPageIsEven ? previousPageIndex : nextPageIndex
}

/**
 * @return {undefined} if there is no sibling
 */
export const getSiblingPageIndex = (
  currentPageIndex: number,
  isMobile: boolean,
): number | undefined => {
  if (isMobile) {
    return undefined
  }
  return currentPageIndex + getSiblingPageDeltaIndex(currentPageIndex)
}

export const getLeftMostCurrentPageIndex = (currentPageIndex: number, isMobile: boolean) => {
  const siblingPageIndex = getSiblingPageIndex(currentPageIndex, isMobile)

  return !isMobile && siblingPageIndex != null
    ? Math.min(currentPageIndex, siblingPageIndex)
    : currentPageIndex
}

export const getRightMostCurrentPageIndex = (currentPageIndex: number, isMobile: boolean) => {
  const siblingPageIndex = getSiblingPageIndex(currentPageIndex, isMobile)

  return !isMobile && siblingPageIndex != null
    ? Math.max(currentPageIndex, siblingPageIndex)
    : currentPageIndex
}

export const getPreviousPageIndex = (pageIndex: number): number | undefined => {
  if (pageIndex === FIRST_PAGE) {
    return undefined
  }

  return pageIndex - 1
}

export const getNextPageIndex = (pageIndex: number, pagesLength: number): number | undefined => {
  if (pageIndex + 1 === pagesLength) {
    return undefined
  }

  return pageIndex + 1
}

export type GetSiblingPagesParams = {
  isMobile: boolean
  currentPageIndex: number
  pages: IMagazinePage[]
}

/**
 * Given device viewport, current slide and set of pages, return if there is a sibling page being shown
 */
export const getSiblingPage = ({ isMobile, currentPageIndex, pages }: GetSiblingPagesParams) => {
  const siblingIndex = getSiblingPageIndex(currentPageIndex, isMobile)

  if (siblingIndex == null) {
    return undefined
  }

  return pages[siblingIndex]
}

export type GetCurrentPagesReturn = {
  currentPage: IMagazinePage
  siblingPage?: IMagazinePage
}

export const getCurrentPages = (params: GetSiblingPagesParams): GetCurrentPagesReturn => {
  const { isMobile, currentPageIndex, pages } = params

  const currentPage = pages[currentPageIndex]

  const siblingPage = getSiblingPage({ isMobile, currentPageIndex, pages })

  return { currentPage, siblingPage }
}

export const getPreviousPages = (params: GetSiblingPagesParams): IMagazinePage[] => {
  const { isMobile, currentPageIndex, pages } = params

  const currentLeftPageIndex = getLeftMostCurrentPageIndex(currentPageIndex, isMobile)

  if (isFirstPageIndex(currentLeftPageIndex)) {
    return []
  }

  const isCurrentSecondPage = isSecondPageIndex(currentLeftPageIndex)

  if (isCurrentSecondPage) {
    return [pages[FIRST_PAGE]]
  }

  const from = currentLeftPageIndex - getPageShownAtTimeCount(isMobile)

  return pages.slice(from, currentLeftPageIndex)
}

export const getNextPages = (params: GetSiblingPagesParams): IMagazinePage[] => {
  const { isMobile, currentPageIndex, pages } = params

  const currentRightPageIndex = getRightMostCurrentPageIndex(currentPageIndex, isMobile)
  const nextPageIndex = getNextPageIndex(currentRightPageIndex, pages.length)

  if (nextPageIndex == null) {
    return []
  }

  const isCurrentPagePenultimate = isPenultimate(currentRightPageIndex, pages.length)

  if (isCurrentPagePenultimate) {
    return pages.slice(-1)
  }

  const to = nextPageIndex + getPageShownAtTimeCount(isMobile)

  return pages.slice(nextPageIndex, to)
}
